/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MsvPipeline.module.scss';

type Translate = (key: string) => string;

// Sanctions-list names (OFAC · EU consolidated · UK OFSI) are proper nouns — left unwrapped.
const getStages = (t: Translate) => [
  {
    num: '01',
    title: t('Submit'),
    body: t('Name, number, country. No document upload, no certificate, no email thread.'),
    meta: t('Seller effort: one form'),
  },
  {
    num: '02',
    title: t('Resolve'),
    body: t(
      'Transliteration, legal-form suffixes and historic names handled. Ambiguity returns candidates with a score.',
    ),
    meta: t('Confidence per candidate'),
  },
  {
    num: '03',
    title: t('Verify'),
    body: t('Each declared field compared to the register value, with source and sync date attached to the field.'),
    meta: t('Field-level provenance'),
  },
  {
    num: '04',
    title: t('Screen'),
    body: t('Entity, officers and beneficial owners in one pass, from the same registry record.'),
    meta: 'OFAC · EU consolidated · UK OFSI',
  },
  {
    num: '05',
    title: t('Monitor'),
    body: t('Status, name, address, officer and ownership events pushed back for as long as the seller trades.'),
    meta: t('Daily-synced change feed'),
  },
];

export const MsvPipeline = () => {
  const { t } = useInt();
  const stages = getStages(t);
  const gridRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const update = () => {
      const grid = gridRef.current;
      const fill = fillRef.current;
      if (!grid || !fill) return;

      const r = grid.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh * 0.86 - r.top) / (r.height + vh * 0.34)));

      const nodes = grid.querySelectorAll('[data-node]');
      if (nodes.length >= 2) {
        const gridR = grid.getBoundingClientRect();
        const lastNode = nodes[nodes.length - 1];
        const lastR = lastNode.getBoundingClientRect();
        const span = Math.max(0, lastR.left - gridR.left + lastR.width / 2);
        const fillWidth = p * span;
        fill.style.width = `${fillWidth}px`;

        stageRefs.current.forEach((el, i) => {
          const nodeEl = nodes[i];
          if (!el || !nodeEl) return;
          const nodeR = nodeEl.getBoundingClientRect();
          const center = nodeR.left - gridR.left + nodeR.width / 2;
          el.classList.toggle(styles.stageOn, fillWidth >= center);
        });
      }
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <Section background="#eef1fa" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Five steps. One call.')}
          description={t(
            'Every verification runs the same path. The response includes every step that ran and the source behind it.',
          )}
          layout="split"
        />
      </div>

      <div className={styles.grid} ref={gridRef}>
        <div className={styles.lineTrack}>
          <div className={styles.lineBg} />
          <div className={styles.lineFill} ref={fillRef} />
        </div>
        {stages.map((stage, i) => (
          <div
            key={stage.num}
            className={styles.stage}
            ref={(el: HTMLDivElement | null) => {
              stageRefs.current[i] = el;
            }}
          >
            <span className={styles.num}>{stage.num}</span>
            <div className={styles.node} data-node="" />
            <h3 className={styles.title}>{stage.title}</h3>
            <p className={styles.body}>{stage.body}</p>
            <div className={styles.meta}>{stage.meta}</div>
          </div>
        ))}
      </div>
    </Section>
  );
};
